import { useEffect, useRef, useState } from 'react';

const STATUS_RESET_MS = 2000;

export type CopyStatus = 'idle' | 'copied' | 'failed';

/**
 * Copies text and reports how that went for two seconds.
 *
 * **Clipboard access is a real boundary**, so a rejected `writeText` (denied
 * permission, an insecure context) becomes the `failed` status rather than a throw
 * — the text is still selectable by hand either way.
 *
 * **`onCopied` runs only when the write succeeded**, so a count of copies is a
 * count of copies and not of attempts a denied clipboard turned away.
 *
 * The timer is cleared on unmount, so a copy followed by navigation does not set
 * state on a component that is gone.
 */
export const useCopyToClipboard = (text: string, onCopied?: () => void) => {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setStatus('copied');
        onCopied?.();
      })
      .catch(() => {
        setStatus('failed');
      })
      .finally(() => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setStatus('idle');
        }, STATUS_RESET_MS);
      });
  };

  return { status, copy };
};
